/**
 * Routes mission claims through the server.
 *
 * `sbClaimMission` (core.js:6360) read the reward off the in-memory mission
 * object and passed it to `sbEarnTokens`. For missions the client *computed*
 * rather than loaded — ids like `um-mission-d1`, with no database row — it also
 * inserted its own `user_missions` row with `claimed: true` and a
 * client-supplied `progress`. Nothing verified the mission was finished.
 *
 * `claim_mission` takes only the mission id and recomputes progress from the
 * rows the mission actually measures, so neither the reward nor the completion
 * is the client's to assert any more.
 */

import { wrapGlobal, legacy, toast } from '../../runtime/legacy.js';

/** Why the server refused, in words a user can act on. */
const REFUSALS = {
  not_complete: "That mission isn't finished yet.",
  mission_not_found: 'That mission is no longer available.',
  progress_type_unsupported: "We can't verify that mission right now.",
  not_authenticated: 'Sign in to claim missions.',
};

/**
 * The RPC keys on the mission catalogue id, but the UI passes the *user*
 * mission id — which for a computed mission is a synthetic `um-` string that
 * exists nowhere in the database.
 */
function resolveMissionId(AppState, userMissionId) {
  const um = (AppState.userMissions || []).find((m) => m.id === userMissionId);
  if (!um) return { um: null, missionId: null };
  return { um, missionId: um.mission_id || um.mission?.id || null };
}

export function installMissionRewards() {
  wrapGlobal('sbClaimMission', async function claim(next, userMissionId) {
    const AppState = legacy('AppState');
    const supabase = legacy('supabaseClient');

    if (legacy('isDemoMode') || !supabase || !AppState?.user) {
      return next(userMissionId);
    }

    const { um, missionId } = resolveMissionId(AppState, userMissionId);
    if (!um || !missionId) {
      console.error('[ironpact] claim_mission: no mission id for', userMissionId);
      return false;
    }

    let result;
    try {
      const { data, error } = await supabase.rpc('claim_mission', {
        p_mission_id: missionId,
      });
      if (error) throw error;
      result = data;
    } catch (err) {
      // No fallback. The legacy path writes token_balance directly, which the
      // Phase 0 guard reverts, so falling back would report a reward that was
      // never paid.
      console.error('[ironpact] claim_mission failed', err);
      toast('Could not claim right now — try again in a moment.', 'error', 4000);
      return false;
    }

    if (!result || result.ok !== true) {
      const reason = result?.error || 'unknown';
      // `not_complete` is ordinary: the UI let them tap a mission the server
      // scores differently. Say so plainly rather than as a failure.
      toast(REFUSALS[reason] || 'Could not claim that mission.', 'error', 4000);
      if (reason === 'not_complete' && typeof result.progress === 'number') {
        um.progress = result.progress;
        um.status = result.progress >= (um.mission?.target ?? Infinity) ? 'completed' : 'in_progress';
      }
      return false;
    }

    if (typeof result.balance === 'number') {
      AppState.tokenBalance = result.balance;
      AppState.profile.token_balance = result.balance;
    }
    um.claimed = true;
    um.status = 'claimed';

    const update = legacy('updateTokenDisplay');
    if (typeof update === 'function') update();

    if (result.already_claimed) {
      // Already paid in this period — the UI was stale. Silent: the user did
      // nothing wrong and has seen the reward once already.
    } else {
      toast(`Claimed ${result.amount} ⬡ tokens!`, 'pr', 4000);
    }
    return true;
  });
}
